import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, ChevronRight, ChevronLeft } from 'lucide-react';
import itemService from '../../services/itemService'; 
import catalogService from '../../services/catalogService'; 
import ProductCard from '../../components/common/ProductCard';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import { getImageUrl } from '../../utils/helpers';

const SearchPage = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const keyword = queryParams.get('q') || '';

    // 👉 TỐI ƯU 1: Tách State lưu Toàn bộ Dữ liệu và State Kết quả lọc
    const [allItems, setAllItems] = useState([]);
    const [adminSpecs, setAdminSpecs] = useState([]); 
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15; 
    
    const [jumpPage, setJumpPage] = useState('');

    // 👉 TỐI ƯU 2: Chỉ Fetch Database 1 lần duy nhất khi Mount. Xóa `keyword` khỏi Dependency.
    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const [resItems, resSpecs] = await Promise.all([
                    itemService.getPublicItems().catch(() => []),
                    catalogService.getSpecs().catch(() => [])
                ]);

                const specsData = Array.isArray(resSpecs?.data) ? resSpecs.data : (Array.isArray(resSpecs) ? resSpecs : []);
                setAdminSpecs(specsData);

                const rawProducts = Array.isArray(resItems?.data) ? resItems.data : (Array.isArray(resItems) ? resItems : []);

                // Lọc bỏ sản phẩm ẩn/hết hạn ngay từ bước đầu
                const validStatuses = new Set([1, 2, 3]); 
                const availableProducts = rawProducts.filter(p => validStatuses.has(p.itemStatus));
                
                setAllItems(availableProducts);
            } catch (error) {
                console.error("Lỗi lấy dữ liệu:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    // 👉 TỐI ƯU 3: Tìm kiếm Instant (Tức thì) sử dụng useMemo thay vì gọi API
    const products = useMemo(() => {
        if (!keyword) return allItems;
        
        const lowerKeyword = keyword.trim().toLowerCase();
        return allItems.filter(p => 
            (p.itemName?.toLowerCase().includes(lowerKeyword)) ||
            (p.itemCode?.toLowerCase().includes(lowerKeyword)) ||
            (String(p.itemId).includes(lowerKeyword))
        );
    }, [allItems, keyword]);

    // Reset trang về 1 mỗi khi đổi từ khóa
    useEffect(() => {
        setCurrentPage(1);
    }, [keyword]);

    const totalPages = Math.ceil(products.length / itemsPerPage) || 1;
    const paginatedProducts = useMemo(() => {
        return products.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    }, [products, currentPage]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            window.scrollTo({ top: 0, behavior: 'smooth' }); 
        }
    };

    const handleJumpPage = () => {
        const page = parseInt(jumpPage, 10);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setJumpPage(''); 
        } else {
            setJumpPage('');
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />

            <main className="flex-grow pb-12">
                <div className="bg-white border-b border-gray-200">
                    <div className="container mx-auto px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                        <Link to="/" className="hover:text-tnc-orange transition-colors">Trang chủ</Link>
                        <ChevronRight size={14} />
                        <span className="font-bold text-gray-800">Tìm kiếm</span>
                    </div>
                </div>

                <div className="container mx-auto px-4 mt-8 max-w-7xl">
                    <div className="bg-orange-50 to-white p-6 rounded-xl shadow-sm border border-orange-200/60 mb-8 flex flex-col md:flex-row justify-between items-center gap-4">
                        <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-3">
                            <div className="p-2.5 bg-white rounded-lg shadow-sm border border-orange-100">
                                <Search size={24} className="text-tnc-orange" />
                            </div>
                            Kết quả tìm kiếm cho: <span className="text-tnc-orange">"{keyword}"</span>
                        </h1>
                        <div className="text-gray-500 font-medium bg-white px-4 py-2 rounded-full border border-orange-100 shadow-sm">
                            Tìm thấy <span className="font-bold text-tnc-orange text-lg">{products.length}</span> sản phẩm
                        </div>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {[...Array(15)].map((_, i) => (
                                <div key={i} className="bg-gray-200 animate-pulse h-80 rounded-xl"></div>
                            ))}
                        </div>
                    ) : products.length > 0 ? (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                {paginatedProducts.map(product => (
                                    <ProductCard 
                                        key={product.itemId} 
                                        product={product} 
                                        getImageUrl={getImageUrl} 
                                        adminSpecs={adminSpecs} 
                                    />
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className="flex flex-wrap justify-center items-center gap-4 mt-10">
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={() => handlePageChange(currentPage - 1)} 
                                            disabled={currentPage === 1}
                                            className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                        >
                                            <ChevronLeft size={16} /> Trang trước
                                        </button>

                                        <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw] scrollbar-hide">
                                            {[...Array(totalPages)].map((_, i) => {
                                                const page = i + 1;
                                                // Rút gọn thuật toán phân trang cơ bản để không bị tràn màn hình nếu >20 trang
                                                if (totalPages > 7 && page !== 1 && page !== totalPages && Math.abs(currentPage - page) > 2) {
                                                    if (page === 2 || page === totalPages - 1) return <span key={page} className="px-2">...</span>;
                                                    return null;
                                                }
                                                
                                                return (
                                                    <button 
                                                        key={page} 
                                                        onClick={() => handlePageChange(page)}
                                                        className={`w-10 h-10 flex-shrink-0 rounded-lg border font-bold transition-colors shadow-sm
                                                            ${currentPage === page 
                                                                ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-tnc-orange hover:border-tnc-orange'
                                                            }`}
                                                    >
                                                        {page}
                                                    </button>
                                                )
                                            })}
                                        </div>

                                        <button 
                                            onClick={() => handlePageChange(currentPage + 1)} 
                                            disabled={currentPage === totalPages}
                                            className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                        >
                                            Trang sau <ChevronRight size={16} />
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-2 border-l border-gray-300 pl-4">
                                        <span className="text-sm text-gray-600 font-medium">Đến trang:</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max={totalPages}
                                            value={jumpPage}
                                            onChange={(e) => setJumpPage(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleJumpPage()}
                                            placeholder={currentPage.toString()}
                                            className="w-16 h-10 px-2 text-center border border-gray-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-gray-700 shadow-sm"
                                        />
                                        <button
                                            onClick={handleJumpPage}
                                            className="px-4 h-10 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors shadow-sm"
                                        >
                                            Đi
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="bg-gradient-to-b from-orange-50 to-white py-24 rounded-2xl shadow-sm text-center border border-orange-200/60 flex flex-col items-center justify-center">
                            <div className="bg-white p-6 rounded-full mb-4 shadow-sm border border-orange-50">
                                <Search className="text-gray-300 w-16 h-16" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-700">Không tìm thấy kết quả nào!</h3>
                            <p className="text-gray-500 mt-2 mb-6 max-w-md">
                                Rất tiếc, chúng tôi không tìm thấy sản phẩm nào phù hợp với từ khóa <span className="font-bold text-tnc-orange">"{keyword}"</span>. Vui lòng thử lại với từ khóa khác.
                            </p>
                            <Link to="/" className="bg-tnc-orange hover:bg-tnc-orange-hover text-white font-bold py-2.5 px-6 rounded-lg transition-colors shadow-sm">
                                Quay lại Trang chủ
                            </Link>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default SearchPage;